<template>
    <v-container class="pb-7 pt-4">
        <v-layout row wrap justify-content-center>
            <v-flex>
                <alert-banner :msg="alertMessage" />
            </v-flex>
        </v-layout>
        <transition name="component-fade" mode="out-in">
            <div v-if="showGeneralInfo" key="generalInfo">
                <v-layout row wrap justify-content-center class="mt-5">
                    <v-flex xs12>
                        <h2 class="text-xs-center text-md-left">
                            Basic Information
                        </h2>
                        <v-divider />
                    </v-flex>
                </v-layout>
                <v-layout row wrap justify-content-center class="mt-5">
                    <v-flex md7 xs11>
                        <h4 class="text-center">
                            Register today to receive updates on all upcoming events and new materials designed to support your growth and success as a CTE Instructor
                        </h4>
                        <v-alert v-model:value="isBlockedDomain" type="error">
                            You must use your teacher email address to register.
                        </v-alert>
                        <form class="mt-5" @submit.prevent="submitGeneralInfo">
                            <v-layout row wrap>
                                <v-flex xs12 sm6 class="px-4">
                                    <v-text-field v-model="generalInfoForm.firstName" v-validate="'required'"
                                        label="First Name*" name="firstName" :error-messages="errors.collect('firstName')"
                                        required />
                                </v-flex>
                                <v-flex xs12 sm6 class="px-4">
                                    <v-text-field v-model="generalInfoForm.lastName" v-validate="'required'"
                                        label="Last Name*" name="lastName" :error-messages="errors.collect('lastName')"
                                        required />
                                </v-flex>
                                <v-flex xs12 sm6 class="px-4">
                                    <v-text-field v-model="generalInfoForm.phone" v-validate="'required'" mask="phone"
                                        label="Phone*" name="phone" :error-messages="errors.collect('phone')" required />
                                </v-flex>
                                <v-flex xs12 sm6 class="px-4">
                                    <v-text-field v-model="generalInfoForm.phoneExtension" label="Phone Extension"
                                        name="phoneExtension" />
                                </v-flex>
                                <v-flex xs12 class="px-4">
                                    <v-select v-model="generalInfoForm.positionTypeId" v-validate="'required'"
                                        :items="positions" label="Which best describes your current position?*"
                                        name="positionTypeId" :error-messages="errors.collect('positionTypeId')" required
                                        item-text="name" item-value="id" />
                                </v-flex>
                                <v-flex v-if="generalInfoForm.positionTypeId === otherPositionId" xs12 class="px-4">
                                    <v-text-field v-model="generalInfoForm.otherPositionTypeDetail" v-validate="'required'"
                                        label="Other Position (Please Specify)*" name="otherPositionTypeDetail"
                                        :error-messages="errors.collect('otherPositionTypeDetail')" required />
                                </v-flex>
                                <v-flex xs12 class="px-4">
                                    <v-select v-model="generalInfoForm.referralTypeId" v-validate="'required'"
                                        :items="referrals" label="How did you learn about KCCTE?*" name="referralTypeId"
                                        :error-messages="errors.collect('referralTypeId')" required item-text="description"
                                        item-value="id" />
                                </v-flex>
                                <v-flex v-if="generalInfoForm.referralTypeId === otherReferralTypeId" xs12 class="px-4">
                                    <v-text-field v-model="generalInfoForm.otherReferralTypeDetail" v-validate="'required'"
                                        label="What other way did you learn about KCCTE?*" name="otherReferralTypeDetail"
                                        :error-messages="errors.collect('otherReferralTypeDetail')" required
                                        item-text="referral type details" />
                                </v-flex>
                                <v-flex xs12 class="px-4">
                                    <v-select v-model="generalInfoForm.programIds" v-validate="'required'" multiple
                                        :items="programs" label="What programs do you work with?*" name="programIds"
                                        :error-messages="errors.collect('programIds')" required item-text="name"
                                        item-value="id" />
                                </v-flex>
                                <v-flex xs12 class="px-4">
                                    <v-text-field ref="emailTextInput" v-model="generalInfoForm.email"
                                        v-validate="'required|email'" label="Work/School Email*" name="email"
                                        :error-messages="errors.collect('email')" type="email" required />
                                </v-flex>
                                <v-flex xs12 sm6 class="px-4">
                                    <v-text-field v-model="generalInfoForm.password" v-validate="'required'"
                                        label="Password*" name="password" :error-messages="errors.collect('password')"
                                        type="password" required />
                                </v-flex>
                                <v-flex xs12 sm6 class="px-4">
                                    <v-text-field v-model="generalInfoForm.passwordConfirmation" v-validate="'required'"
                                        label="Confirm Password*" name="passwordConfirmation"
                                        :error-messages="errors.collect('passwordConfirmation')" type="password" required />
                                </v-flex>
                            </v-layout>
                            <div class="text-right mt-4">
                                <v-btn href="/" flat>
                                    Cancel
                                </v-btn>
                                <v-btn type="submit" color="secondary" :loading="saving">
                                    Next
                                </v-btn>
                            </div>
                        </form>
                    </v-flex>
                </v-layout>
            </div>
            <div v-else-if="showInstitutionPage" key="institutionPage">
                <v-layout row wrap justify-content-center class="mt-5">
                    <v-flex xs12>
                        <h2 class="text-xs-center text-md-left">
                            Institution Information
                        </h2>
                        <v-divider />
                    </v-flex>
                </v-layout>
                <v-layout row wrap justify-content-center class="mt-5">
                    <v-flex md7 xs11>
                        <v-alert :value="true" type="info">
                            If your insitution doesn't show up in the dropdown list, start typing your institution name in
                            the dropdown and then click "+Create New Institution" to add it to our system. Once it is added,
                            you'll be able to select it from the dropdown.'
                        </v-alert>
                        <form class="mt-5" @submit.prevent="submitInstutition">
                            <v-layout row wrap>
                                <v-flex xs12 class="px-4">
                                    <v-autocomplete v-model="institutionPage.institutionId" v-validate="'required'"
                                        :items="institutions" class="mx-1" hide-details label="Institution Name*"
                                        name="institutionId" :error-messages="errors.collect('institutionId')" required
                                        item-text="name" item-value="id">
                                        <template v-slot:no-data>
                                            <v-list-tile @click="addInstitutionDialog = true">
                                                <v-list-tile-title>
                                                    + Create New Insitution
                                                </v-list-tile-title>
                                            </v-list-tile>
                                        </template>
                                    </v-autocomplete>
                                </v-flex>
                                <v-flex v-if="institutionPage.institutionId" xs12 class="px-4 mt-4">
                                    <v-alert :value="true" type="info">
                                        If your campus doesn't show up in the dropdown list, start typing your campus name
                                        (e.g. "Main Campus") in the dropdown and then click "+Create New Campus" to add it
                                        to our system. Most institutions will just have one campus, but some have multiple.
                                        Once it is added, you'll be able to select it from the dropdown. If you just added
                                        your institution above, you'll need to add a campus.'
                                    </v-alert>

                                    <v-autocomplete v-model="institutionPage.campusId" v-validate="'required'"
                                        :items="campuses" class="mx-1" hide-details label="Campus Name*" name="campusId"
                                        :error-messages="errors.collect('campusId')" required item-text="name"
                                        item-value="id">
                                        <template v-slot:no-data>
                                            <v-list-tile @click="addCampusDialog = true">
                                                <v-list-tile-title>
                                                    + Create New Campus
                                                </v-list-tile-title>
                                            </v-list-tile>
                                        </template>
                                    </v-autocomplete>
                                </v-flex>
                            </v-layout>
                            <div class="text-right mt-4">
                                <v-btn flat @click="goBackToGeneralInfo">
                                    Back
                                </v-btn>
                                <v-btn type="submit" color="secondary" :loading="saving">
                                    Submit
                                </v-btn>
                            </div>
                        </form>
                    </v-flex>
                </v-layout>
                <add-institution-modal :dialog.sync="addInstitutionDialog" @updateInstitutions="updateInstitutions" />
                <add-campus-modal :dialog.sync="addCampusDialog" :institution-id="institutionPage.institutionId"
                    @updateCampuses="updateCampuses" />
            </div>
            <div v-else-if="showConfirmPage" key="confirmPage">
                <v-layout row wrap justify-content-center class="mt-5">
                    <v-flex xs12>
                        <h2 class="text-xs-center text-md-left">
                            Confirmation
                        </h2>
                        <v-divider />
                    </v-flex>
                </v-layout>
                <v-layout row wrap justify-content-center class="mt-1">
                    <v-flex xs11 md12 class="text-xs-center text-md-left">
                        <h3 class="mb-4 font-weight-bold">
                            Thank you!
                        </h3>
                        <p v-if="generalInfoForm.makeAuthor">
                            We have received your author access request and will contact you shortly.
                        </p>
                        <p>
                            You have successfully registered for the KCCTE Curriculum Library.
                        </p>
                        <p>
                            Please check your email to confirm your email to get started.
                        </p>
                    </v-flex>
                </v-layout>
            </div>
        </transition>
    </v-container>
</template>

<script>
import axios from 'axios';
import AddInstitutionModal from './components/AddInstitutionModal.vue';
import AddCampusModal from './components/AddCampusModal.vue';

export default {
    parent: true,

    components: {
        AddInstitutionModal,
        AddCampusModal,
    },

    data: () => ({
        isBlockedDomain: false,

        generalInfoForm: {
            firstName: '',
            lastName: '',
            phone: '',
            phoneExtension: '',
            email: '',
            positionTypeId: '',
            referralTypeId: '',
            otherReferralTypeDetail: '',
            password: '',
            passwordConfirmation: '',
            makeAuthor: false,
            programIds: [],
            otherPositionTypeDetail: '',
        },

        institutionPage: {
            institutionId: '',
            campusId: '',
        },

        showGeneralInfo: true,
        showInstitutionPage: false,
        showConfirmPage: false,
        addInstitutionDialog: false,
        addCampusDialog: false,
        saving: false,
        referrals: [],
        positions: [],
        programs: [],
        institutions: [],
        campuses: [],
    }),

    computed: {
        otherPositionId() {
            const positions = JSON.parse(JSON.stringify(this.positions));
            const position = positions.find(x => x.name.includes('Other'));
            if (position) return position.id;

            return -1;
        },
        otherReferralTypeId() {
            const referrals = JSON.parse(JSON.stringify(this.referrals));
            const referral = referrals.find(x => x.description.includes('Other'));
            if (referral) return referral.id;

            return -1;
        },
    },

    watch: {
        'institutionPage.institutionId': function () {
            this.getCampuses();
        },
    },

    mounted() {
        this.checkForExistingSession();
        this.getReferralTypes();
        this.getPrograms();
        this.getPositionTypes();
        this.getInstitutions();
        this.$validator.pause();
    },

    methods: {
        async getReferralTypes() {
            const { data } = await axios.get('/api/registration/referral-types');
            this.referrals = data;
        },

        async checkForExistingSession() {
            try {
                const { data } = await axios.get('/api/register');
                Object.keys(this.generalInfoForm).filter(key => key in data).forEach(key => this.generalInfoForm[key] = data[key]);
            } catch (e) {
                // No existing registered user was found.
            }
        },

        async getPositionTypes() {
            const { data } = await axios.get('/api/registration/position-types');
            this.positions = data;
        },

        async getPrograms() {
            const { data } = await axios.get('/api/programs');
            this.programs = data;
        },

        async getInstitutions() {
            const { data } = await axios.get('/api/institutions');
            this.institutions = data;
        },

        async getCampuses() {
            this.institutionPage.campusId = '';
            const { data } = await axios.get(`/api/institutions/${this.institutionPage.institutionId}/campuses`);
            this.campuses = data;
        },

        async submitGeneralInfo() {
            const { data } = await axios.get(`/api/blocked-email-domains/${this.generalInfoForm.email}`);
            const isBlockedEmailDomain = data;
            if (isBlockedEmailDomain) {
                this.isBlockedDomain = true;
                this.$refs.emailTextInput.focus();

                return;
            }

            if (this.generalInfoForm.referralTypeId !== this.otherReferralTypeId) {
                this.generalInfoForm.otherReferralTypeDetail = '';
            }
            if (this.generalInfoForm.positionTypeId !== this.otherPositionId) {
                this.generalInfoForm.otherPositionTypeDetail = '';
            }

            const result = await this.startFormProcessing();
            if (!result) return;
            this.saving = true;
            try {
                await axios.post('/api/register/general-info', this.generalInfoForm);
                this.showGeneralInfo = false;
                this.showInstitutionPage = true;
            } catch (error) {
                this.processErrors(error, this.generalInfoForm);
            } finally {
                this.saving = false;
            }
        },

        goBackToGeneralInfo() {
            this.showGeneralInfo = true;
            this.showInstitutionPage = false;
        },

        async submitInstutition() {
            const result = await this.startFormProcessing();
            if (!result) return;
            this.saving = true;
            try {
                await axios.post(`/api/register/campus?campusId=${this.institutionPage.campusId}`);
                await axios.post('/api/register/complete');
                this.showInstitutionPage = false;
                this.showConfirmPage = true;
            } catch (error) {
                this.processErrors(error, {
                    campusId: '',
                });
            } finally {
                this.saving = false;
            }
        },

        updateInstitutions(data) {
            this.institutions.unshift(data);
        },

        updateCampuses(data) {
            this.campuses.unshift(data);
        },
    },
};
</script>

<style scoped>
.component-fade-enter-active,
.component-fade-leave-active {
    transition: opacity .2s ease;
}

.component-fade-enter,
.component-fade-leave-to {
    opacity: 0;
}
</style>